import React, { useMemo, useState } from "react";
import { useDrawerRecord } from "hooks/useDrawerRecord";
import Button from "@mui/material/Button";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  DateRangeFilter,
  EMPTY_DATE_RANGE,
  SelectFilter,
  TablePage,
} from "@outpave/ds";
import type { DateRangeValue, FilterOption } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { PlusSignIcon } from "@hugeicons-pro/core-stroke-rounded";
import useDebouncedSearch from "hooks/useDebouncedSearch";
import useGetParamFromUrl from "hooks/useGetParamFromUrl";
import useShowReimbursements from "hooks/useShowReimbursements";
import useEmployeeFilterOptions from "hooks/useEmployeeFilterOptions";
import { useAdminTools } from "hooks/useAdminTools";
import { Inline } from "components/layout/reusableLayouts";
import {
  useDateRangeFilterLabels,
  useDateRangePresets,
} from "utils/dateRangeFilter";
import AmountRangeChip, {
  type AmountRange,
} from "components/common/AmountRangeChip";
import TransactionDetailsDrawer from "components/common/transactions/TransactionDetailsDrawer";
import ReimbursementDrawer from "components/common/reimbursements/ReimbursementDrawer";
import ReimbursementsTable from "components/common/reimbursements/ReimbursementsTable";
import { REIMBURSEMENT_TABS } from "utils/reimbursements/reimbursementRows";
import { REIMBURSEMENT_STATUSES } from "utils/transactions/transactionGroups";
import { reimbursementStatusLabel } from "components/common/RenderTransactionStatus";
import TransactionStatus from "enums/TransactionStatus";

const TABS = [REIMBURSEMENT_TABS.OUTSTANDING, REIMBURSEMENT_TABS.ALL];

const EMPTY_AMOUNT_RANGE: AmountRange = {};

type ReimbursementsPageProps = {
  /** The viewer's own reimbursements, for /my; otherwise the company's. */
  mine?: boolean;
};

const ReimbursementsPage: React.FC<ReimbursementsPageProps> = ({
  mine = false,
}) => {
  const { t } = useTranslation();
  const showReimbursements = useShowReimbursements();
  const adminTools = useAdminTools();

  const [activeTabKey, setActiveTabKey] = useState<string>(TABS[0].key);
  const [searchTerm, setSearchTerm] = useState("");
  const search = useDebouncedSearch(setSearchTerm);
  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [amountRange, setAmountRange] =
    useState<AmountRange>(EMPTY_AMOUNT_RANGE);
  const [dateRange, setDateRange] = useState<DateRangeValue>(EMPTY_DATE_RANGE);
  const datePresets = useDateRangePresets();
  const dateLabels = useDateRangeFilterLabels();
  const [openCreateDrawer, setOpenCreateDrawer] = useState(false);
  const transactionDrawer = useDrawerRecord<string>();
  const [refreshReimbursements, setRefreshReimbursements] = useState(0);

  const employeeOptions = useEmployeeFilterOptions({
    skip: !showReimbursements || !adminTools,
  });

  const activeTab =
    TABS.find((tab) => tab.key === activeTabKey) ?? REIMBURSEMENT_TABS.ALL;

  // A tab is a hard constraint; the chip narrows within it rather than
  // widening past it, so it only ever offers what the tab can show.
  const tabStatuses = useMemo<TransactionStatus[]>(
    () => (activeTab.status ? [...activeTab.status] : REIMBURSEMENT_STATUSES),
    [activeTab],
  );

  const statusOptions: FilterOption[] = useMemo(
    () =>
      tabStatuses.map((status) => ({
        value: status,
        label: t(reimbursementStatusLabel(status)),
      })),
    [tabStatuses, t],
  );

  // The picks this tab can show; the rest stay picked for a tab that shows them.
  const statusPicks = useMemo(
    () =>
      selectedStatuses.filter((status) =>
        tabStatuses.includes(status as TransactionStatus),
      ),
    [selectedStatuses, tabStatuses],
  );

  const filterCriteria = useMemo(
    () => ({
      status:
        statusPicks.length > 0
          ? (statusPicks as TransactionStatus[])
          : tabStatuses,
      searchTerm,
      cardHolder: adminTools ? selectedEmployee : null,
      amountRange,
      dateRange,
    }),
    [
      statusPicks,
      tabStatuses,
      searchTerm,
      selectedEmployee,
      adminTools,
      amountRange,
      dateRange,
    ],
  );

  const handleOpenDrawer = (id: string) => transactionDrawer.show(id);

  const resetParam = useGetParamFromUrl("id", handleOpenDrawer);

  const handleCloseDrawer = () => {
    transactionDrawer.close();
    resetParam();
  };

  const handleRefresh = () => setRefreshReimbursements((prev) => prev + 1);

  const handleCloseCreateDrawer = (refresh?: boolean) => {
    setOpenCreateDrawer(false);
    if (refresh) handleRefresh();
  };

  // A tenant without Stripe issuing or the entitlement has no reimbursements
  // to approve, and the nav item is hidden, so a typed URL lands on the ledger.
  if (!showReimbursements) {
    return (
      <Navigate
        to={mine ? "/my/transactions" : "/admin/transactions"}
        replace
      />
    );
  }

  return (
    <TablePage
      title={t("Reimbursements")}
      subtitle={t(
        mine
          ? "My reimbursements page subtitle"
          : "Reimbursements page subtitle",
      )}
      primaryAction={
        <Button
          onClick={() => setOpenCreateDrawer(true)}
          startIcon={<HugeiconsIcon icon={PlusSignIcon} size={16} />}
        >
          {t("New reimbursement")}
        </Button>
      }
      tabs={{
        items: TABS.map((tab) => ({ id: tab.key, label: t(tab.label) })),
        activeId: activeTab.key,
        onChange: setActiveTabKey,
      }}
      search={{ ...search, placeholder: t("Search reimbursements") }}
      filters={
        <Inline gap="var(--space-3)" align="center">
          {adminTools && (
            <SelectFilter
              label={t("Employee")}
              options={employeeOptions}
              selected={selectedEmployee}
              onChange={setSelectedEmployee}
            />
          )}
          <DateRangeFilter
            label={t("Date range")}
            presets={datePresets}
            labels={dateLabels}
            value={dateRange}
            onChange={setDateRange}
          />
          <AmountRangeChip
            label={t("Amount")}
            value={amountRange}
            onChange={setAmountRange}
          />
          <SelectFilter
            label={t("Status")}
            options={statusOptions}
            selected={statusPicks}
            onChange={setSelectedStatuses}
            multiple
          />
        </Inline>
      }
    >
      <ReimbursementsTable
        mine={mine}
        filterCriteria={filterCriteria}
        handleOpenDrawer={handleOpenDrawer}
        refreshReimbursements={refreshReimbursements}
      />

      {transactionDrawer.record && (
        <TransactionDetailsDrawer
          open={transactionDrawer.open}
          handleCloseDrawer={handleCloseDrawer}
          onAfterClose={transactionDrawer.afterClose}
          transactionId={transactionDrawer.record}
          handleRefreshTransactions={handleRefresh}
        />
      )}

      <ReimbursementDrawer
        open={openCreateDrawer}
        choosePayee={adminTools}
        onClose={() => handleCloseCreateDrawer()}
        onSubmitted={handleRefresh}
      />
    </TablePage>
  );
};

export default ReimbursementsPage;
